<script setup lang="ts">
interface Distance {
  label: string;
  km: number;
  minutes: number;
  transport: string;
  mode: "walk" | "taxi" | "bus" | "drive";
  note?: string;
  recommended?: boolean;
  lat?: number;
  lng?: number;
}

interface DistancesBlock {
  title?: string;
  intro?: string;
  summary?: string;
  items: Distance[];
}

defineProps<{
  block: DistancesBlock;
  activeIndex?: number | null;
}>();

const emit = defineEmits<{
  (e: "row:hover", index: number | null): void;
  (e: "row:focus", index: number): void;
}>();

const { t, n } = useI18n();

const formatKm = (km: number) => {
  return km < 1 ? km.toFixed(1) : n(km);
};
</script>

<template>
  <section class="relative overflow-hidden bg-white dark:bg-slate-900">
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />

    <div class="mx-auto w-full max-w-5xl px-4 py-10 md:py-16">
      <header data-reveal class="mb-10 md:mb-14">
        <div
          class="flex items-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500"
        >
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t("location.distances.eyebrow") }}</span>
        </div>
        <h2
          class="mt-4 max-w-3xl font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.title || t("location.distances.default_title") }}
        </h2>
        <!-- Two-column deck: framing intro on the left, the distance-fact
             prose (summary) on the right, so the header fills the width above
             the full-width table instead of leaving the right half empty.
             Collapses to a single stacked column on mobile; falls back to one
             column if only one of the two paragraphs is present. -->
        <div
          v-if="block.intro || block.summary"
          class="mt-6 grid gap-x-10 gap-y-4 md:mt-7 lg:gap-x-16"
          :class="{ 'md:grid-cols-2': block.intro && block.summary }"
        >
          <p
            v-if="block.intro"
            class="font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
          >
            {{ block.intro }}
          </p>
          <p
            v-if="block.summary"
            class="font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
            data-speakable
          >
            {{ block.summary }}
          </p>
        </div>
      </header>

      <div
        data-reveal
        class="grid grid-cols-[auto_1fr_auto_auto] items-baseline gap-x-3 gap-y-0 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500 sm:gap-x-4 md:grid-cols-[auto_1fr_auto_auto_1fr] md:gap-x-6"
      >
        <span class="sr-only md:not-sr-only">{{
          t("location.distances.headers.mode")
        }}</span>
        <span>{{ t("location.distances.headers.place") }}</span>
        <span class="text-right">{{
          t("location.distances.headers.km")
        }}</span>
        <span class="text-right">{{
          t("location.distances.headers.minutes")
        }}</span>
        <span class="hidden md:block">{{
          t("location.distances.headers.transport")
        }}</span>
      </div>

      <ol class="mt-3">
        <li
          v-for="(item, i) in block.items"
          :key="i"
          class="group relative grid grid-cols-[auto_1fr_auto_auto] items-baseline gap-x-3 border-t border-slate-200/80 py-5 font-body text-slate-900 transition-colors duration-300 hover:text-amber-500 sm:gap-x-4 md:grid-cols-[auto_1fr_auto_auto_1fr] md:gap-x-6 md:py-6 dark:border-slate-700/60 dark:text-white dark:hover:text-amber-400"
          :class="{
            'text-amber-500 dark:text-amber-400': activeIndex === i,
            'cursor-pointer': item.lat != null && item.lng != null,
          }"
          @mouseenter="item.lat != null && emit('row:hover', i)"
          @mouseleave="emit('row:hover', null)"
          @click="item.lat != null && emit('row:focus', i)"
        >
          <span
            aria-hidden="true"
            class="absolute -left-2 bottom-0 h-0 w-[calc(100%+1rem)] bg-amber-400/5 transition-all duration-300 group-hover:h-full"
            :class="{ 'h-full': activeIndex === i }"
          />

          <span
            class="relative flex h-7 w-7 items-center justify-center text-slate-400 transition-colors duration-300 group-hover:text-amber-500 sm:h-8 sm:w-8 md:h-9 md:w-9 dark:text-slate-500 dark:group-hover:text-amber-400"
            :aria-label="t(`location.distances.mode.${item.mode}`)"
          >
            <svg
              v-if="item.mode === 'walk'"
              viewBox="0 0 24 24"
              class="h-5 w-5 md:h-6 md:w-6"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="13" cy="4" r="2" />
              <path
                d="M9.5 22l1.8-5.5L8 13l1-5 3 1 2 3 3 1M7 10l3-1 3 3 2 6"
              />
            </svg>
            <svg
              v-else-if="item.mode === 'bus'"
              viewBox="0 0 24 24"
              class="h-5 w-5 md:h-6 md:w-6"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="4" y="5" width="16" height="12" rx="2" />
              <path d="M4 11h16M8 17v2M16 17v2" />
              <circle cx="8" cy="14" r="1" />
              <circle cx="16" cy="14" r="1" />
            </svg>
            <svg
              v-else-if="item.mode === 'drive'"
              viewBox="0 0 24 24"
              class="h-5 w-5 md:h-6 md:w-6"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 15l1.6-5.2a2 2 0 012-1.3h6.8a2 2 0 012 1.3L19 15" />
              <rect x="3" y="14" width="18" height="4" rx="1" />
              <circle cx="7.5" cy="18.5" r="1.2" />
              <circle cx="16.5" cy="18.5" r="1.2" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              class="h-5 w-5 md:h-6 md:w-6"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 15l1.6-5.2a2 2 0 012-1.3h6.8a2 2 0 012 1.3L19 15" />
              <rect x="3" y="14" width="18" height="4" rx="1" />
              <path d="M9 7h6" />
              <circle cx="7.5" cy="18.5" r="1.2" />
              <circle cx="16.5" cy="18.5" r="1.2" />
            </svg>
          </span>

          <span class="relative min-w-0">
            <span class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span class="font-display text-base sm:text-lg md:text-2xl">
                {{ item.label }}
              </span>
              <span
                v-if="item.recommended"
                class="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none text-amber-700 dark:bg-amber-400/15 dark:text-amber-300"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  class="h-2.5 w-2.5"
                  fill="currentColor"
                >
                  <path
                    d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.51L10 14.13l-4.94 2.6.94-5.51-4-3.9 5.53-.8z"
                  />
                </svg>
                {{ t("location.distances.recommended") }}
              </span>
            </span>
            <span
              v-if="item.note"
              class="mt-1 block text-xs uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500"
            >
              {{ item.note }}
            </span>
          </span>

          <span class="relative text-right tabular-nums">
            <span
              class="font-display text-2xl font-semibold text-amber-500 md:text-3xl dark:text-amber-400"
            >
              {{ formatKm(item.km) }}
            </span>
            <span
              class="ml-1 text-xs uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500"
            >
              {{ t("location.distances.units.km") }}
            </span>
          </span>

          <span class="relative text-right tabular-nums">
            <span class="text-base md:text-lg">{{ item.minutes }}</span>
            <span
              class="ml-1 text-xs uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500"
            >
              {{ t("location.distances.units.min") }}
            </span>
          </span>

          <span
            class="relative col-span-4 mt-2 text-sm text-slate-500 md:col-span-1 md:mt-0 md:text-right md:text-base dark:text-slate-400"
          >
            {{ item.transport }}
          </span>
        </li>
      </ol>
    </div>
  </section>
</template>
